/**
 * A contractor's name that opens the Contractor Profile drill-down (Details, Works,
 * Payments & Dues, Invoices, …) when clicked. Use it wherever WMS shows a contractor.
 *
 *   <ContractorLink id={row.cntrctr_id} name={row.cntrctr_nm} />
 *
 * With an id it opens straight away. With only a name (screens whose API sends no
 * cntrctr_id) it looks the contractor up by exact name on click. A name that matches
 * no contractor (e.g. a Tally ledger name) stays plain text after the attempt.
 * The profile is portalled to <body> so a transformed grid cell cannot clip it, and
 * lazy-loaded so screens don't pay for it until it is opened.
 */
import { lazy, Suspense, useState, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { contractorsApi } from '../services/wmsService';

const ContractorProfile = lazy(() => import('./profiles/ContractorProfile'));

const norm = (s: unknown) => String(s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

interface Props {
  id?: number | string | null;
  name?: string | null;
  code?: string | null;
  /** Rendered in place of the name, e.g. a styled badge. */
  children?: ReactNode;
  className?: string;
  /** Shown when there is no name. */
  fallback?: ReactNode;
}

export default function ContractorLink({ id, name, code, children, className = '', fallback = '-' }: Props) {
  const [openId, setOpenId] = useState<number | null>(null);
  const [resolving, setResolving] = useState(false);
  const [unmatched, setUnmatched] = useState(false);

  const label = children ?? name;
  const idNum = Number(id);
  const hasId = Number.isFinite(idNum) && idNum > 0;
  if (!label || (typeof label === 'string' && !label.trim())) return <>{fallback}</>;
  if (unmatched || (!hasId && !name)) return <span className={className} title={unmatched ? 'Not found in the contractor master' : undefined}>{label}</span>;

  const open = async (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (hasId) { setOpenId(idNum); return; }
    setResolving(true);
    try {
      const res: any = await contractorsApi.getAll({ search: String(name).trim() }, 1, 50);
      const list: any[] = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      const hit = list.find((c) => norm(c.cntrctr_nm) === norm(name) || norm(c.frm_nm) === norm(name));
      if (hit?.cntrctr_id) setOpenId(Number(hit.cntrctr_id)); else setUnmatched(true);
    } catch {
      setUnmatched(true);
    } finally {
      setResolving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={open}
        title="View contractor details, works and payments"
        className={`max-w-full cursor-pointer truncate text-left text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 ${resolving ? 'opacity-60' : ''} ${className}`}
      >
        {label}
      </button>
      {openId !== null && createPortal(
        <Suspense fallback={null}>
          <ContractorProfile
            isOpen
            onClose={() => setOpenId(null)}
            contractor={{ contractorId: openId, name: name || undefined, contractorCode: code || undefined }}
          />
        </Suspense>,
        document.body,
      )}
    </>
  );
}
